// src/experiments/debugOverlay.js
//
// A small on-screen panel answering "which version am I seeing, and why":
// every experiment with the arm this browser is in, the effective traffic,
// where each value came from (code, cached remote, fresh remote, or a QA
// override), the remote document's status, config validation messages, the
// session exposures and the visitor id. Plain DOM, no React, so it works on
// any page and can never take the app down with it.
//
// Available in development, or in production after a one-off opt-in with
// `?expdebug=1` on any URL (remembered for the tab). Toggle with
// Ctrl/Cmd+Shift+X, or close with the button or Escape.

import { getGeneration, getVariant, subscribe } from './assignments';
import { groupOf, isActive, validateExperiments } from './assign';
import { getContext } from './context';
import { EXPERIMENTS } from './experiments.config';
import { getFlagsState } from './flags';
import { getOverrides } from './overrides';
import { getExperiments, getMeta } from './registry';
import { CACHE_KEY, getRemoteStatus } from './remoteConfig';
import { getSessionExposures } from './sessionExposures';
import { getUid } from './uid';

export const DEBUG_PARAM = 'expdebug';
export const DEBUG_SESSION_KEY = 'hippo_exp_debug';
const PANEL_ID = 'hippo-exp-debug';
const REFRESH_MS = 1000;

let panel = null;
let timer = null;
let unsubscribe = null;

function readFlag() {
  try {
    return sessionStorage.getItem(DEBUG_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

// `?expdebug=1` turns the overlay on for this tab; `?expdebug=0` turns it
// off again. Returns whether the query string asked for it right now.
export function captureDebugOptIn(search) {
  try {
    const params = new URLSearchParams(search || '');
    if (!params.has(DEBUG_PARAM)) return false;
    const wanted = params.get(DEBUG_PARAM) !== '0';
    if (wanted) sessionStorage.setItem(DEBUG_SESSION_KEY, '1');
    else sessionStorage.removeItem(DEBUG_SESSION_KEY);
    return wanted;
  } catch {
    return false;
  }
}

export function isDebugAllowed({ env = process.env.NODE_ENV } = {}) {
  return env !== 'production' || readFlag();
}

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function ago(timestamp) {
  if (!timestamp) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 90) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

function cacheAge() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const cached = raw ? JSON.parse(raw) : null;
    return cached && typeof cached.fetchedAt === 'number'
      ? ago(cached.fetchedAt)
      : 'none';
  } catch {
    return 'unreadable';
  }
}

function targetingOf(experiment) {
  const parts = [];
  if (experiment.paths?.length)
    parts.push(`paths ${experiment.paths.join(' ')}`);
  if (experiment.products?.length) {
    parts.push(`products ${experiment.products.join(' ')}`);
  }
  if (experiment.device)
    parts.push(`device ${[].concat(experiment.device).join(' ')}`);
  return parts.join(' · ') || 'everywhere';
}

// Builds the panel's inner HTML from live state. Exported for tests.
export function renderDebugHtml() {
  const ctx = getContext();
  const overrides = getOverrides();
  const exposures = getSessionExposures();
  const remote = getRemoteStatus();
  const errors = validateExperiments(EXPERIMENTS);

  const rows = getExperiments().map((experiment) => {
    const meta = getMeta(experiment.key);
    const arm = getVariant(experiment.key);
    const source = Object.prototype.hasOwnProperty.call(
      overrides,
      experiment.key
    )
      ? 'override'
      : meta.source;
    return `<tr>
      <td><b>${esc(experiment.key)}</b></td>
      <td>${arm === null ? '<i>not enrolled</i>' : esc(arm)}</td>
      <td>${esc(experiment.traffic)}</td>
      <td>${esc(source)}${meta.force !== 'none' ? ` · force ${esc(meta.force)}${meta.hard ? ' (hard)' : ''}` : ''}</td>
      <td>${isActive(experiment) ? 'active' : 'dormant'} · ${esc(groupOf(experiment))}</td>
      <td>${esc(targetingOf(experiment))}</td>
    </tr>`;
  });

  const list = (object) =>
    Object.keys(object).length
      ? Object.entries(object)
          .map(([key, value]) => `${esc(key)}:${esc(value)}`)
          .join(' ')
      : '<i>none</i>';

  return `
    <div data-role="head">
      <b>Experiments</b>
      <span>${esc(ctx.pathname)} · ${esc(ctx.device)}${ctx.isBot ? ' · <b>crawler UA: always control</b>' : ''}</span>
      <button type="button" data-role="close" aria-label="Close experiments panel">×</button>
    </div>
    <table>
      <thead><tr><th>key</th><th>my arm</th><th>traffic</th><th>source</th><th>state · group</th><th>targeting</th></tr></thead>
      <tbody>${rows.join('') || '<tr><td colspan="6"><i>no experiments in config</i></td></tr>'}</tbody>
    </table>
    <table>
      <thead><tr><th>flag</th><th>value</th><th>default</th><th>source</th></tr></thead>
      <tbody>${
        getFlagsState()
          .map(
            (flag) =>
              `<tr><td><b>${esc(flag.key)}</b></td><td>${esc(String(flag.value))}</td><td>${esc(String(flag.defaultValue))}</td><td>${esc(flag.source)}</td></tr>`
          )
          .join('') || '<tr><td colspan="4"><i>no flags in config</i></td></tr>'
      }</tbody>
    </table>
    <div data-role="rows">
      <div><b>remote</b> ${esc(remote.fetch)}${remote.message ? ` · ${esc(remote.message)}` : ''} · experiments ${esc(remote.experiments)} · flags ${esc(remote.flags)} · in effect: ${esc(remote.applied)}${remote.pending ? ' · new document waiting for a route change' : ''} · fetched ${esc(ago(remote.fetchedAt))} · cache ${esc(cacheAge())}${remote.ignored?.length ? ` · ignored keys ${esc(remote.ignored.join(', '))}` : ''}</div>
      <div><b>config</b> ${errors.length ? esc(errors.join(' | ')) : 'valid'}</div>
      <div><b>overrides</b> ${list(overrides)}</div>
      <div><b>seen this session</b> ${list(exposures)}</div>
      <div><b>uid</b> ${esc(getUid())} · generation ${esc(getGeneration())}</div>
      <div><i>?exp=key:arm forces an arm, ?flag=key:value forces a flag, for this tab · ?exp=clear / ?flag=clear forget them · Ctrl/Cmd+Shift+X toggles this panel</i></div>
    </div>`;
}

const STYLE = `
  position:fixed;left:8px;bottom:8px;z-index:2147483000;max-width:min(96vw,980px);
  max-height:60vh;overflow:auto;background:#111a22;color:#e6ecf0;font:12px/1.45
  ui-monospace,Menlo,Consolas,monospace;border:1px solid #34485a;border-radius:6px;
  padding:10px 12px;box-shadow:0 8px 24px rgba(0,0,0,.4);`;
const INNER_STYLE = `
  #${PANEL_ID} [data-role=head]{display:flex;gap:12px;align-items:baseline;margin-bottom:6px}
  #${PANEL_ID} [data-role=head] span{color:#8fa0ab;flex:1}
  #${PANEL_ID} [data-role=close]{background:none;border:1px solid #34485a;color:#e6ecf0;border-radius:4px;cursor:pointer;font-size:14px;line-height:1;padding:2px 7px}
  #${PANEL_ID} table{border-collapse:collapse;width:100%;margin:4px 0 8px}
  #${PANEL_ID} th,#${PANEL_ID} td{text-align:left;padding:3px 10px 3px 0;border-bottom:1px solid #243441;vertical-align:top}
  #${PANEL_ID} th{color:#8fa0ab;font-weight:600}
  #${PANEL_ID} [data-role=rows] div{margin:2px 0}
  #${PANEL_ID} i{color:#8fa0ab}`;

function refresh() {
  if (!panel) return;
  try {
    panel.innerHTML = `<style>${INNER_STYLE}</style>${renderDebugHtml()}`;
  } catch (err) {
    panel.innerHTML = `<style>${INNER_STYLE}</style><div>experiments overlay failed to render: ${esc(err?.message || err)}</div>`;
  }
}

function onKeyDown(event) {
  if (event.key === 'Escape') closeDebugOverlay();
}

export function isDebugOverlayOpen() {
  return !!panel;
}

export function openDebugOverlay() {
  try {
    if (typeof document === 'undefined' || panel) return false;
    panel = document.createElement('section');
    panel.id = PANEL_ID;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Experiments debug panel');
    panel.setAttribute('style', STYLE);
    panel.addEventListener('click', (event) => {
      if (event.target?.getAttribute?.('data-role') === 'close') {
        closeDebugOverlay();
      }
    });
    document.body.appendChild(panel);
    document.addEventListener('keydown', onKeyDown);
    unsubscribe = subscribe(refresh);
    timer = setInterval(refresh, REFRESH_MS);
    refresh();
    return true;
  } catch {
    return false;
  }
}

export function closeDebugOverlay() {
  try {
    if (timer) clearInterval(timer);
    if (unsubscribe) unsubscribe();
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', onKeyDown);
    }
    if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
  } catch {
    // ignore
  }
  timer = null;
  unsubscribe = null;
  panel = null;
}

export function toggleDebugOverlay(options) {
  if (!isDebugAllowed(options)) return false;
  if (panel) {
    closeDebugOverlay();
    return false;
  }
  return openDebugOverlay();
}

// Boot hook: remember a `?expdebug=1` opt-in and open the panel straight
// away when the URL asks for it.
export function bootDebugOverlay() {
  try {
    const requested = captureDebugOptIn(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    if (requested && isDebugAllowed()) openDebugOverlay();
  } catch {
    // ignore
  }
}
